<template>
  <div class="deco-app-layout">
    <!-- ヘッダー: ガイド/アラート + 完成ボタン -->
    <header class="deco-header">
      <div class="deco-header__info">
        <span v-if="showOverAlert" class="deco-header__alert">⚠ 画質が悪くなる可能性があります</span>
        <span v-else-if="isFrameLimited" class="deco-header__warn">この商品は専用フレームのご利用をおすすめします</span>
        <span v-else class="deco-header__guide">写真を選択後、フレーム・スタンプ・文字で装飾しよう</span>
      </div>
      <button class="deco-finish-btn" @click="uiStore.showPanel('preview')">確認する</button>
    </header>

    <!-- キャンバスエリア -->
    <div class="deco-canvas-area" @click.self="canvasStore.deselectAll()">
      <DecoCanvas ref="decoCanvasRef" />
    </div>

    <!-- ツールバー -->
    <ToolBar />

    <!-- プロパティバー (選択中オブジェクトがある時 - fixed配置) -->
    <CommandBar />

    <!-- パネル (フルスクリーンオーバーレイ) -->
    <DesignPanel />
    <FramePanel />
    <StampPanel />
    <TextPanel />
    <HistoryPanel />
    <PreviewPanel />
    <LoadingOverlay />
  </div>
</template>

<script setup>
import { ref, provide, onMounted, onBeforeUnmount, onUnmounted } from 'vue'
import { useDecoDataStore } from './stores/useDecoDataStore'
import { useCanvasStore } from './stores/useCanvasStore'
import { useOrderStore } from './stores/useOrderStore'
import { useUiStore } from './stores/useUiStore'
import { useApplyDesign } from './composables/useApplyDesign'
import DecoCanvas from './components/canvas/DecoCanvas.vue'
import ToolBar from './components/panels/ToolBar.vue'
import CommandBar from './components/panels/CommandBar.vue'
import DesignPanel from './components/panels/DesignPanel.vue'
import FramePanel from './components/panels/FramePanel.vue'
import StampPanel from './components/panels/StampPanel.vue'
import TextPanel from './components/panels/TextPanel.vue'
import HistoryPanel from './components/panels/HistoryPanel.vue'
import PreviewPanel from './components/panels/PreviewPanel.vue'
import LoadingOverlay from './components/ui/LoadingOverlay.vue'

const decoDataStore = useDecoDataStore()
const canvasStore = useCanvasStore()
const orderStore = useOrderStore()
const uiStore = useUiStore()

const isFrameLimited = ref(false)
const showOverAlert = ref(false)

const decoCanvasRef = ref(null)

// PreviewPanel が stageRef にアクセスできるように provide
const stageRef = ref(null)
provide('stageRef', stageRef)

// イベントリスナー (アンマウント時に解除)
const onBeforeUnload = (e) => {
  if (uiStore.isSubmitting) return
  if (canvasStore.objects.length > 0) {
    e.preventDefault()
    e.returnValue = ''
  }
}
const onResize = () => canvasStore.updateScale()

onMounted(async () => {
  // ページを開いてからデフォルトデザイン(画像/フォント)の読み込みが終わるまで
  // ローディングを表示する
  uiStore.setLoading(true)
  try {
    const data = window.__DECO_DATA__ ?? {}
    decoDataStore.init(data)
    canvasStore.initStage(data.aspectRatio ?? 1.0, !!data.isCircularLabel)
    orderStore.init(data)
    uiStore.detectPcMode()
    isFrameLimited.value = !!(data.context?.isFrameLimited)

    if (uiStore.isPcMode) document.body.classList.add('pc_mode')

    window.addEventListener('beforeunload', onBeforeUnload)
    window.addEventListener('resize', onResize)

    // デフォルトデザイン: 現在選択中グループ (既定は先頭) を配置
    // 複数該当する場合はツールバーの「デザイン」ボタンから切り替え可能
    if (decoDataStore.activeDesignGroups.length > 0) {
      const { applySelectedDesign } = useApplyDesign()
      await applySelectedDesign()
    }
  } finally {
    uiStore.setLoading(false)
  }
})

onUnmounted(() => {
  window.removeEventListener('beforeunload', onBeforeUnload)
  window.removeEventListener('resize', onResize)
})

// DecoCanvas マウント後に stageRef をセット
onMounted(() => {
  const timer = setInterval(() => {
    if (decoCanvasRef.value?.stageRef) {
      stageRef.value = decoCanvasRef.value.stageRef
      clearInterval(timer)
    }
  }, 50)
})
</script>
